import { PlusIcon } from '@heroicons/react/24/outline';
import { SearchEntity } from '@plato/search/search-types';
import DraftIcon from '@plato/ui-legacy/assets/icons/draft-icon';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { ChevronDownIcon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerBasics } from '../../../server/types/infer-trpc-types.ts';
import { Can } from '../../acl/can.tsx';
import { useInvalidateFeed } from '../../activity-feed/hooks/use-activity-actions.ts';
import { DropdownMenuItemCreateEvent } from '../../events/components/dropdown-menu-item-create-event.tsx';
import { DropdownMenuItemCreateNote } from '../../notes/components/dropdown-menu-item-create-note.tsx';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';
import { DropdownMenuItemCreateTask } from '../../tasks/components/dropdown-menu-item-create-task.tsx';

type Props = {
    customer: CustomerBasics | undefined;
};

export function CustomerDetailNewActivity({ customer }: Props) {
    const { t } = useTranslation();
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const dropdownTriggerRef = React.useRef(null);
    const invalidateFeed = useInvalidateFeed();

    const handleNewNoteCreated = () => {
        invalidateFeed();
    };

    return (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger
                render={
                    <Button
                        disabled={!customer}
                        variant="subtle"
                        ref={dropdownTriggerRef}
                        leftIcon={PlusIcon}
                        rightIcon={ChevronDownIcon}
                        rightIconProps={{
                            rotateOnActive: true,
                        }}
                    >
                        <span>{t('customer_detail__header__new_activity', { ns: 'customer-detail' })}</span>
                    </Button>
                }
            />
            <DropdownMenuContent align="end" side="bottom" className="min-w-(--anchor-width)">
                <DropdownMenuItemCreateTask
                    links={
                        customer
                            ? [
                                  {
                                      modelType: SearchEntity.Customer,
                                      modelId: customer.customerId,
                                      title: customer.customerName ?? null,
                                  },
                              ]
                            : []
                    }
                    source="customer_detail"
                />

                <Can I="create" a="SalesDraft">
                    <DropdownMenuItem
                        onClick={() => {
                            void DialogStateManager.show(DraftOrderCreateDialog, {
                                customerId: customer?.customerId,
                                source: 'customer_detail',
                            });
                        }}
                    >
                        <MenuItemLeading>
                            <DraftIcon />
                        </MenuItemLeading>
                        <MenuItemLabel>
                            {t('customer_detail__header__draft', { ns: 'customer-detail' })}
                        </MenuItemLabel>
                    </DropdownMenuItem>
                </Can>
                <DropdownMenuItemCreateEvent customer={customer} analyticsSource="customer" />
                <DropdownMenuItemCreateNote
                    customer={customer}
                    onSuccess={handleNewNoteCreated}
                    analyticsSource="customer_detail"
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
